import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Brain, ArrowLeft, Building2, User as UserIcon, Calendar,
  AlertTriangle, Loader2, Trash2, FileDown,
  Pause, FolderPlus, Scale, RefreshCw, ExternalLink, Play, Search, FileSignature,
  Share2, Copy, CheckCircle2, Eye, XCircle, Mail, QrCode, Bell,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { obtenerToken } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

const CAT_COLOR = {
  A: 'bg-green-100 text-green-800 border-green-300',
  B: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  C: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  D: 'bg-orange-100 text-orange-800 border-orange-300',
  E: 'bg-blue-100 text-blue-800 border-blue-300',
  X: 'bg-purple-100 text-purple-800 border-purple-300',
};

const CAT_LABEL = {
  A: '≥90% (MUY ALTA)',
  B: '70-89% (ALTA)',
  C: '50-69% (MEDIA)',
  D: '25-49% (BAJA-MEDIA)',
  E: 'Complementaria',
  X: 'No catalogada',
};

const formatEur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v || 0);

const tokenHeader = () => ({ headers: { Authorization: `Bearer ${obtenerToken()}` } });

// Descarga binaria (PDF/DOCX) o texto (MD) preservando filename
async function descargarArchivo(url, suggestedName) {
  const res = await axios.get(url, { ...tokenHeader(), responseType: 'blob' });
  const blob = res.data;
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  // Intentar leer Content-Disposition
  const cd = res.headers['content-disposition'] || '';
  const m = cd.match(/filename="([^"]+)"/);
  link.download = m ? m[1] : suggestedName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

export default function AnalisisHipotecaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configModulo, setConfigModulo] = useState(null);

  // Modales
  const [crearExpedienteOpen, setCrearExpedienteOpen] = useState(false);
  const [vincularOpen, setVincularOpen] = useState(false);
  const [plantillaOpen, setPlantillaOpen] = useState(false);
  const [compartirOpen, setCompartirOpen] = useState(false);

  const [downloadingFormato, setDownloadingFormato] = useState(null);
  const [accionLoading, setAccionLoading] = useState(false);
  const [procesoStartAt, setProcesoStartAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/analisis/${id}`, tokenHeader());
      setA(r.data);
      setLoadError(null);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message || 'Error desconocido';
      console.error('[Detalle] load error', e);
      setLoadError(`${status ? `HTTP ${status} — ` : ''}${detail}`);
      toast({ title: 'Error', description: 'No se pudo cargar el análisis', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Cargar config del módulo una vez (para mostrar/ocultar feature Compartir)
  useEffect(() => {
    axios.get(`${API}/api/hipotecas/configuracion`, tokenHeader())
      .then((r) => setConfigModulo(r.data))
      .catch(() => setConfigModulo({ enlace_publico_habilitado: false }));
  }, []);

  // Polling si estado en proceso + arrancar contador de tiempo + tick 1s
  useEffect(() => {
    if (!a) return;
    const enProc = ['analizando', 'convirtiendo'].includes(a.estado);
    if (enProc) {
      if (!procesoStartAt) setProcesoStartAt(Date.now());
      const tPoll = setTimeout(load, 3000);
      const tTick = setInterval(() => setNowTick(Date.now()), 1000);
      return () => { clearTimeout(tPoll); clearInterval(tTick); };
    } else {
      if (procesoStartAt) setProcesoStartAt(null);
    }
  }, [a, load, procesoStartAt]);

  const cancelarAnalisis = async () => {
    if (!window.confirm('¿Cancelar el análisis en curso? Se descartará y podrás reintentarlo más tarde.')) return;
    setAccionLoading(true);
    try {
      await axios.post(`${API}/api/hipotecas/analisis/${id}/cancelar`, {}, tokenHeader());
      toast({ title: 'Análisis cancelado' });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setAccionLoading(false);
    }
  };

  const reanalizar = async () => {
    if (!window.confirm('¿Volver a lanzar el análisis con la IA? Sobrescribirá los resultados.')) return;
    try {
      await axios.post(`${API}/api/hipotecas/analisis/${id}/analizar`, {}, tokenHeader());
      toast({ title: 'Análisis re-iniciado' });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const borrar = async () => {
    if (!window.confirm('¿Borrar este análisis permanentemente?')) return;
    try {
      await axios.delete(`${API}/api/hipotecas/analisis/${id}`, tokenHeader());
      toast({ title: 'Eliminado' });
      navigate('/expedientes/analisis-hipotecas');
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const exportar = async (formato) => {
    setDownloadingFormato(formato);
    try {
      await descargarArchivo(`${API}/api/hipotecas/analisis/${id}/export/${formato}`, `analisis_hipoteca_${id}.${formato}`);
      toast({ title: `Informe ${formato.toUpperCase()} descargado` });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setDownloadingFormato(null);
    }
  };

  const abrirOriginal = async () => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/analisis/${id}/archivo-original?as=url`, tokenHeader());
      if (r.data?.url) {
        window.open(r.data.url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('Sin URL');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo abrir la escritura original', variant: 'destructive' });
    }
  };

  const pausar = async () => {
    setAccionLoading(true);
    try {
      await axios.post(`${API}/api/hipotecas/analisis/${id}/pausar`, {}, tokenHeader());
      toast({ title: 'Análisis pausado' });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setAccionLoading(false);
    }
  };

  const reactivar = async () => {
    setAccionLoading(true);
    try {
      await axios.post(`${API}/api/hipotecas/analisis/${id}/reactivar`, {}, tokenHeader());
      toast({ title: 'Análisis reactivado' });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setAccionLoading(false);
    }
  };

  const crearReclamacion = async () => {
    if (!window.confirm('¿Crear una reclamación extrajudicial precargada con los datos del análisis?')) return;
    setAccionLoading(true);
    try {
      const r = await axios.post(`${API}/api/hipotecas/analisis/${id}/crear-reclamacion-extrajudicial`, {}, tokenHeader());
      toast({ title: 'Reclamación creada', description: r.data.numero_referencia });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setAccionLoading(false);
    }
  };

  if (loading) {
    return <div className="flex flex-col items-center py-20 gap-3"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /><p className="text-sm text-gray-500">Cargando análisis #{id}…</p></div>;
  }
  if (loadError) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2">
              <XCircle className="w-6 h-6 text-red-600" />
              <h2 className="font-semibold text-red-900">No se pudo cargar el análisis</h2>
            </div>
            <p className="text-sm text-red-800">{loadError}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/expedientes/analisis-hipotecas')}>
                <ArrowLeft className="w-4 h-4 mr-1" />Volver
              </Button>
              <Button onClick={() => { setLoading(true); setLoadError(null); load(); }}>
                <RefreshCw className="w-4 h-4 mr-1" />Reintentar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!a) {
    return <div className="p-6">No encontrado.</div>;
  }

  const datos = a.datos_hipoteca || {};
  const prestatarios = datos.prestatarios || [];
  const enProceso = ['analizando', 'convirtiendo'].includes(a.estado);
  const yaTieneExpediente = !!a.expediente_id;
  const yaTieneReclamacion = !!a.reclamacion_extrajudicial_id;
  const isPausado = a.estado === 'pausado';

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="analisis-detalle">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/expedientes/analisis-hipotecas')} data-testid="btn-back">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Volver
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Brain className="w-6 h-6 text-purple-600" />
              {a.nombre_archivo_original}
            </h1>
            <p className="text-xs text-gray-500">
              Análisis #{a.id} · {a.provider}/{a.model} · {a.duracion_segundos}s · {a.tokens_input}→{a.tokens_output} tokens
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {a.s3_key_original && (
            <Button variant="outline" size="sm" onClick={abrirOriginal} data-testid="btn-ver-original" className="border-slate-300 text-slate-700 hover:bg-slate-50">
              <Eye className="w-4 h-4 mr-1" />
              Ver escritura
            </Button>
          )}
          {(a.estado === 'analizado' || a.estado === 'expediente_creado' || a.estado === 'expediente_anadido' || a.estado === 'extrajudicial_creado' || a.estado === 'pausado') && (
            <>
              <Button variant="outline" size="sm" onClick={() => exportar('pdf')} disabled={downloadingFormato === 'pdf'} data-testid="btn-export-pdf-header">
                {downloadingFormato === 'pdf' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
                PDF
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportar('docx')} disabled={downloadingFormato === 'docx'} data-testid="btn-export-docx-header">
                {downloadingFormato === 'docx' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
                DOCX
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPlantillaOpen(true)} data-testid="btn-generar-plantilla-header">
                <FileSignature className="w-4 h-4 mr-1" />
                Plantilla
              </Button>
              {configModulo?.enlace_publico_habilitado && (
                <Button variant="outline" size="sm" onClick={() => setCompartirOpen(true)} data-testid="btn-compartir-header" className="border-purple-300 text-purple-700 hover:bg-purple-50">
                  <Share2 className="w-4 h-4 mr-1" />
                  Compartir
                </Button>
              )}
            </>
          )}
          {a.estado === 'analizado' && (
            <Button variant="outline" size="sm" onClick={reanalizar} data-testid="btn-reanalizar">
              <RefreshCw className="w-4 h-4 mr-1" />
              Re-analizar
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={borrar} data-testid="btn-borrar">
            <Trash2 className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      </div>

      {/* Estado en proceso */}
      {enProceso && (() => {
        const elapsed = procesoStartAt ? Math.floor((nowTick - procesoStartAt) / 1000) : 0;
        const mm = Math.floor(elapsed / 60).toString().padStart(2, '0');
        const ss = (elapsed % 60).toString().padStart(2, '0');
        const fase = a.estado === 'convirtiendo' ? 1 : 2;
        const estimacionTotalSeg = 120;
        const indeterminado = elapsed > 180; // tras 3 min, modo indeterminado
        const progreso = indeterminado ? 100 : Math.min(95, Math.floor((elapsed / estimacionTotalSeg) * 100));
        return (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-7 h-7 animate-spin text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-blue-900">
                      {a.paso_actual ? a.paso_actual : (
                        a.estado === 'convirtiendo'
                          ? 'Paso 1 de 2 — Convirtiendo documento a Markdown…'
                          : 'Paso 2 de 2 — Analizando hipoteca con IA…'
                      )}
                    </p>
                    <p className="text-sm text-blue-700 mt-0.5">
                      Tiempo transcurrido: <span className="font-mono">{mm}:{ss}</span>
                      {' · '}Esta página se actualiza sola cada 3 s.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelarAnalisis}
                  disabled={accionLoading}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="btn-cancelar-analisis"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Cancelar
                </Button>
              </div>

              {/* Pasos visuales */}
              <div className="flex items-center gap-2 text-xs">
                <div className={`flex items-center gap-1 px-2 py-1 rounded ${fase >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {fase > 1 ? <CheckCircle2 className="w-3 h-3" /> : <Loader2 className="w-3 h-3 animate-spin" />}
                  Conversión a MD
                </div>
                <div className="flex-1 h-0.5 bg-gray-300" />
                <div className={`flex items-center gap-1 px-2 py-1 rounded ${fase >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {fase >= 2 ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className="w-3 h-3 rounded-full bg-gray-400" />}
                  Análisis IA ({a.provider || '...'} {a.model ? '/' + a.model : ''})
                </div>
              </div>

              {/* Barra de progreso: determinada hasta 180s, después sweep indeterminado */}
              {indeterminado ? (
                <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden relative">
                  <div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-[sweep_1.6s_ease-in-out_infinite]" />
                  <style>{`@keyframes sweep { 0% { left: -33%; } 100% { left: 100%; } }`}</style>
                </div>
              ) : (
                <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 transition-all duration-1000"
                    style={{ width: `${progreso}%` }}
                  />
                </div>
              )}
              <p className="text-xs text-gray-600">
                {indeterminado ? (
                  <>
                    ⚠ El análisis está tardando más de lo habitual (más de 3 min). Esto puede pasar con
                    escrituras muy extensas o cuando Claude Sonnet está bajo carga.
                    Sigue procesando — el timeout máximo es 10 minutos. Si quieres puedes
                    cancelar y reintentarlo más tarde o cambiar de modelo.
                  </>
                ) : (
                  <>
                    ⏱ El análisis suele tardar entre 60 y 180 segundos según el tamaño del documento.
                    Puedes salir de esta pantalla — el proceso continúa en segundo plano.
                  </>
                )}
              </p>
            </CardContent>
          </Card>
        );
      })()}

      {/* Pausado */}
      {isPausado && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Pause className="w-6 h-6 text-yellow-700" />
              <div>
                <p className="font-medium">Análisis en pausa</p>
                <p className="text-sm text-gray-600">No se aplicará ninguna acción hasta que lo reactives.</p>
              </div>
            </div>
            <Button size="sm" onClick={reactivar} disabled={accionLoading} data-testid="btn-reactivar">
              <Play className="w-4 h-4 mr-1" />Reactivar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Vinculado */}
      {yaTieneExpediente && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <FolderPlus className="w-5 h-5 text-emerald-700" />
              <div>
                <p className="font-medium">Vinculado al expediente <span className="font-mono">{a.expediente_numero || `#${a.expediente_id}`}</span></p>
                <p className="text-xs text-gray-600">Estado: {a.estado}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate(`/expedientes/${a.expediente_id}`)} data-testid="btn-ir-expediente">
              <ExternalLink className="w-4 h-4 mr-1" />Ver expediente
            </Button>
          </CardContent>
        </Card>
      )}

      {yaTieneReclamacion && (
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Scale className="w-5 h-5 text-purple-700" />
              <div>
                <p className="font-medium">Reclamación extrajudicial #{a.reclamacion_extrajudicial_id} creada</p>
                <p className="text-xs text-gray-600">Datos pre-cargados del análisis</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/reclamaciones-extrajudiciales')} data-testid="btn-ir-reclamacion">
              <ExternalLink className="w-4 h-4 mr-1" />Ver reclamaciones
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {a.estado === 'error' && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <p className="font-medium text-red-800"><AlertTriangle className="w-4 h-4 inline mr-2" />Error en el análisis</p>
            <p className="text-sm text-red-700 mt-2">{a.error_mensaje}</p>
          </CardContent>
        </Card>
      )}

      {/* Sin OCR */}
      {a.estado === 'sin_ocr' && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-4">
            <p className="font-medium text-orange-800"><AlertTriangle className="w-4 h-4 inline mr-2" />PDF sin texto extraíble (escaneado)</p>
            <p className="text-sm text-orange-700 mt-2">{a.error_mensaje}</p>
          </CardContent>
        </Card>
      )}

      {/* Datos de la hipoteca */}
      {(a.estado === 'analizado' || a.estado === 'expediente_creado' || a.estado === 'expediente_anadido' || a.estado === 'extrajudicial_creado' || a.estado === 'pausado') && (
        <>
          <Card data-testid="datos-hipoteca">
            <CardHeader>
              <CardTitle className="text-base">Datos de la hipoteca</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <Info label="Notario" value={datos.notario} />
                <Info label="Protocolo" value={datos.protocolo} />
                <Info label="Fecha firma" value={datos.fecha_firma} icon={<Calendar className="w-3 h-3" />} />
                <Info label="Régimen" value={a.regimen_aplicable} />
                <Info label="Banco" value={datos.entidad_prestamista} icon={<Building2 className="w-3 h-3" />} />
                <Info label="Capital" value={datos.capital_eur ? formatEur(datos.capital_eur) : null} />
                <Info label="Plazo" value={datos.plazo_anios ? `${datos.plazo_anios} años` : null} />
                <Info label="Tipo" value={datos.tipo_interes} />
                <Info label="Índice" value={datos.indice_referencia} />
                <Info label="Diferencial" value={datos.diferencial ? `${datos.diferencial}%` : null} />
                <Info label="Apertura €" value={datos.comision_apertura_eur ? formatEur(datos.comision_apertura_eur) : null} />
                <Info label="Apertura %" value={datos.comision_apertura_pct ? `${datos.comision_apertura_pct}%` : null} />
                {(datos.comisiones_adicionales || [])
                  .filter((c) => c && (c.importe_eur || c.porcentaje || c.concepto))
                  .map((c, idx) => {
                    const partes = [];
                    if (c.importe_eur) partes.push(formatEur(c.importe_eur));
                    if (c.porcentaje) partes.push(`${c.porcentaje}%`);
                    return (
                      <Info
                        key={`com-${idx}`}
                        label={c.concepto || 'Comisión'}
                        value={partes.length ? partes.join(' · ') : null}
                      />
                    );
                  })}
              </div>

              {prestatarios.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    <UserIcon className="w-4 h-4 inline mr-1" />Prestatarios
                  </p>
                  <div className="space-y-2">
                    {prestatarios.map((p, idx) => (
                      <div key={idx} className="text-sm bg-gray-50 p-2 rounded">
                        <span className="font-medium">{p.nombre || '—'}</span>
                        {p.dni && <span className="text-gray-500"> · DNI {p.dni}</span>}
                        {p.direccion && <span className="text-gray-500"> · {p.direccion}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Resumen económico */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100" data-testid="resumen-clausulas">
              <CardContent className="p-4">
                <p className="text-xs text-purple-600 font-medium">CLÁUSULAS DETECTADAS</p>
                <p className="text-3xl font-bold text-purple-900 mt-1">{a.num_clausulas_detectadas}</p>
                <p className="text-xs text-purple-700 mt-1">{a.num_clausulas_alta_viabilidad} con alta viabilidad (≥70%)</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100" data-testid="resumen-recuperable">
              <CardContent className="p-4">
                <p className="text-xs text-emerald-600 font-medium">RECUPERABLE</p>
                <p className="text-3xl font-bold text-emerald-900 mt-1">{formatEur(a.importe_total_recuperable)}</p>
                <p className="text-xs text-emerald-700 mt-1">+ {formatEur(a.importe_total_con_intereses - a.importe_total_recuperable)} intereses</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100" data-testid="resumen-total">
              <CardContent className="p-4">
                <p className="text-xs text-blue-600 font-medium">TOTAL CON INTERESES LEGALES</p>
                <p className="text-3xl font-bold text-blue-900 mt-1">{formatEur(a.importe_total_con_intereses)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs detalle */}
          <Tabs defaultValue="clausulas" className="w-full">
            <TabsList>
              <TabsTrigger value="clausulas" data-testid="tab-clausulas">Cláusulas ({a.clausulas.length})</TabsTrigger>
              <TabsTrigger value="informe" data-testid="tab-informe">Informe</TabsTrigger>
              <TabsTrigger value="acciones" data-testid="tab-acciones">Acciones</TabsTrigger>
            </TabsList>

            <TabsContent value="clausulas" className="space-y-3">
              {a.clausulas.map((c) => (
                <Card key={c.id} className="border-l-4" style={{ borderLeftColor: c.viabilidad_pct >= 90 ? '#10b981' : c.viabilidad_pct >= 70 ? '#3b82f6' : c.viabilidad_pct >= 50 ? '#f59e0b' : '#9ca3af' }} data-testid={`clausula-${c.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          {c.codigo && <Badge className="font-mono text-xs">{c.codigo}</Badge>}
                          <Badge className={CAT_COLOR[c.categoria] || ''}>{CAT_LABEL[c.categoria] || c.categoria}</Badge>
                          <span className="font-semibold">{c.nombre}</span>
                        </div>
                        {c.localizacion && (
                          <p className="text-xs text-gray-500 mt-1">{c.localizacion}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-purple-700">{c.viabilidad_pct}%</p>
                        {c.impacto_eur > 0 && <p className="text-sm font-medium text-emerald-700">{formatEur(c.impacto_eur)}</p>}
                      </div>
                    </div>

                    {c.descripcion_breve && (
                      <p className="mt-3 text-sm text-gray-700">{c.descripcion_breve}</p>
                    )}
                    {c.fundamento_juridico && (
                      <div className="mt-2 text-xs bg-gray-50 p-2 rounded">
                        <span className="font-medium text-gray-700">Fundamento jurídico:</span>{' '}
                        <span className="text-gray-600">{c.fundamento_juridico}</span>
                      </div>
                    )}
                    {c.jurisprudencia && (
                      <p className="mt-2 text-xs text-blue-700">
                        <Scale className="w-3 h-3 inline mr-1" />
                        {c.jurisprudencia}
                      </p>
                    )}
                    {c.riesgos && (
                      <p className="mt-2 text-xs text-orange-700">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        <span className="font-medium">Riesgos:</span> {c.riesgos}
                      </p>
                    )}
                    {c.texto_clausula && (
                      <details className="mt-2 text-xs">
                        <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Texto original de la escritura</summary>
                        <pre className="mt-2 p-2 bg-gray-50 rounded whitespace-pre-wrap text-gray-700">{c.texto_clausula}</pre>
                      </details>
                    )}
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="informe">
              <Card>
                <CardContent className="p-6">
                  {a.informe_markdown ? (
                    <pre className="whitespace-pre-wrap text-sm font-sans text-gray-800 leading-relaxed" data-testid="informe-markdown">
                      {a.informe_markdown}
                    </pre>
                  ) : (
                    <p className="text-gray-500 text-center py-6">Informe no disponible aún</p>
                  )}
                  <div className="mt-4 pt-4 border-t flex flex-wrap gap-2">
                    <Button variant="outline" onClick={() => exportar('pdf')} disabled={downloadingFormato === 'pdf'} data-testid="btn-export-pdf">
                      {downloadingFormato === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                      Descargar PDF
                    </Button>
                    <Button variant="outline" onClick={() => exportar('docx')} disabled={downloadingFormato === 'docx'} data-testid="btn-export-docx">
                      {downloadingFormato === 'docx' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                      Descargar DOCX
                    </Button>
                    <Button variant="outline" onClick={() => exportar('md')} disabled={downloadingFormato === 'md'} data-testid="btn-export-md">
                      {downloadingFormato === 'md' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                      Descargar MD
                    </Button>
                    <Button variant="outline" onClick={() => setPlantillaOpen(true)} data-testid="btn-generar-plantilla">
                      <FileSignature className="w-4 h-4 mr-2" />
                      Generar desde plantilla…
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="acciones">
              <Card>
                <CardContent className="p-6 space-y-3">
                  <p className="text-sm text-gray-600">Decide qué hacer con este análisis:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      disabled={yaTieneExpediente || accionLoading}
                      onClick={() => setCrearExpedienteOpen(true)}
                      className="justify-start h-auto py-3"
                      data-testid="btn-crear-expediente"
                    >
                      <FolderPlus className="w-5 h-5 mr-3 text-emerald-600" />
                      <div className="text-left">
                        <p className="font-semibold">Crear expediente nuevo</p>
                        <p className="text-xs text-gray-500">{yaTieneExpediente ? `Ya vinculado a ${a.expediente_numero || `#${a.expediente_id}`}` : 'Crea un expediente precargando datos'}</p>
                      </div>
                    </Button>
                    <Button
                      variant="outline"
                      disabled={yaTieneExpediente || accionLoading}
                      onClick={() => setVincularOpen(true)}
                      className="justify-start h-auto py-3"
                      data-testid="btn-anadir-expediente"
                    >
                      <ExternalLink className="w-5 h-5 mr-3 text-blue-600" />
                      <div className="text-left">
                        <p className="font-semibold">Añadir a expediente existente</p>
                        <p className="text-xs text-gray-500">{yaTieneExpediente ? 'Ya vinculado' : 'Selecciona expediente del despacho'}</p>
                      </div>
                    </Button>
                    <Button
                      variant="outline"
                      disabled={yaTieneReclamacion || accionLoading}
                      onClick={crearReclamacion}
                      className="justify-start h-auto py-3"
                      data-testid="btn-extrajudicial"
                    >
                      <Scale className="w-5 h-5 mr-3 text-purple-600" />
                      <div className="text-left">
                        <p className="font-semibold">Crear reclamación extrajudicial</p>
                        <p className="text-xs text-gray-500">{yaTieneReclamacion ? `Creada #${a.reclamacion_extrajudicial_id}` : 'Genera reclamación precargada'}</p>
                      </div>
                    </Button>
                    <Button
                      variant="outline"
                      disabled={isPausado || accionLoading}
                      onClick={pausar}
                      className="justify-start h-auto py-3"
                      data-testid="btn-pausar"
                    >
                      <Pause className="w-5 h-5 mr-3 text-yellow-600" />
                      <div className="text-left">
                        <p className="font-semibold">{isPausado ? 'Ya en pausa' : 'Dejar en pausa'}</p>
                        <p className="text-xs text-gray-500">Conserva el análisis sin acciones</p>
                      </div>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Modal: Crear expediente nuevo */}
      <CrearExpedienteDialog
        open={crearExpedienteOpen}
        onClose={() => setCrearExpedienteOpen(false)}
        analisis={a}
        onCreated={() => { setCrearExpedienteOpen(false); load(); }}
      />

      {/* Modal: Vincular a expediente existente */}
      <VincularExpedienteDialog
        open={vincularOpen}
        onClose={() => setVincularOpen(false)}
        analisisId={a.id}
        onLinked={() => { setVincularOpen(false); load(); }}
      />

      {/* Modal: Generar desde plantilla */}
      <GenerarDesdePlantillaDialog
        open={plantillaOpen}
        onClose={() => setPlantillaOpen(false)}
        analisisId={a.id}
      />

      {/* Modal: Compartir informe con cliente */}
      <CompartirInformeDialog
        open={compartirOpen}
        onClose={() => setCompartirOpen(false)}
        analisisId={a.id}
      />
    </div>
  );
}

function Info({ label, value, icon }) {
  return (
    <div>
      <p className="text-xs text-gray-500 flex items-center gap-1">{icon}{label}</p>
      <p className="font-medium text-gray-800">{value || '—'}</p>
    </div>
  );
}

// ============================================================================
// Modal: Crear expediente nuevo
// ============================================================================
function CrearExpedienteDialog({ open, onClose, analisis, onCreated }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    numero_expediente: '',
    titulo: '',
    rama: 'Civil',
    tipo_procedimiento: 'Préstamo hipotecario - Cláusulas abusivas',
    fase: 'Estudio',
  });

  useEffect(() => {
    if (open && analisis) {
      const datos = analisis.datos_hipoteca || {};
      const prest = datos.prestatarios?.[0]?.nombre || 'Cliente';
      setForm((f) => ({
        ...f,
        titulo: f.titulo || `Cláusulas abusivas hipoteca ${datos.entidad_prestamista || ''} (${prest})`,
      }));
    }
  }, [open, analisis]);

  const submit = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/hipotecas/analisis/${analisis.id}/crear-expediente`, form, tokenHeader());
      toast({ title: 'Expediente creado', description: r.data.numero_expediente });
      onCreated();
      navigate(`/expedientes/${r.data.expediente_id}`);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dialog-crear-expediente">
        <DialogHeader>
          <DialogTitle>Crear expediente nuevo</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nº expediente (opcional, se autogenera)</Label>
            <Input
              value={form.numero_expediente}
              onChange={(e) => setForm({ ...form, numero_expediente: e.target.value })}
              placeholder="Ej. HIP-2026-001"
              data-testid="input-numero-expediente"
            />
          </div>
          <div>
            <Label>Título *</Label>
            <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} data-testid="input-titulo" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Rama</Label>
              <Input value={form.rama} onChange={(e) => setForm({ ...form, rama: e.target.value })} data-testid="input-rama" />
            </div>
            <div>
              <Label>Fase</Label>
              <Input value={form.fase} onChange={(e) => setForm({ ...form, fase: e.target.value })} data-testid="input-fase" />
            </div>
          </div>
          <div>
            <Label>Tipo de procedimiento</Label>
            <Input value={form.tipo_procedimiento} onChange={(e) => setForm({ ...form, tipo_procedimiento: e.target.value })} data-testid="input-tipo-procedimiento" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={loading || !form.titulo} data-testid="btn-confirmar-crear-expediente">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderPlus className="w-4 h-4 mr-2" />}
            Crear expediente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Modal: Vincular a expediente existente
// ============================================================================
function VincularExpedienteDialog({ open, onClose, analisisId, onLinked }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const buscar = useCallback(async (term) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/hipotecas/expedientes-buscar?q=${encodeURIComponent(term)}&limit=20`, tokenHeader());
      setResults(r.data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) buscar('');
  }, [open, buscar]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => buscar(q), 300);
    return () => clearTimeout(t);
  }, [q, open, buscar]);

  const vincular = async (expId) => {
    try {
      const r = await axios.post(`${API}/api/hipotecas/analisis/${analisisId}/vincular-expediente`, { expediente_id: expId }, tokenHeader());
      toast({ title: 'Vinculado', description: r.data.numero_expediente });
      onLinked();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="dialog-vincular-expediente">
        <DialogHeader>
          <DialogTitle>Vincular a expediente existente</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <Input
              autoFocus
              placeholder="Buscar por número o título…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="input-buscar-expediente"
            />
          </div>
          <div className="max-h-80 overflow-auto border rounded">
            {loading ? (
              <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-purple-600" /></div>
            ) : results.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">No hay resultados.</p>
            ) : (
              results.map((e) => (
                <button
                  key={e.id}
                  onClick={() => vincular(e.id)}
                  className="w-full text-left p-3 hover:bg-purple-50 border-b last:border-b-0 transition"
                  data-testid={`expediente-result-${e.id}`}
                >
                  <p className="font-medium text-sm">{e.numero_expediente} <Badge variant="outline" className="ml-1">{e.rama}</Badge></p>
                  <p className="text-xs text-gray-500 truncate">{e.titulo}</p>
                </button>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Modal: Generar documento desde plantilla
// ============================================================================
function GenerarDesdePlantillaDialog({ open, onClose, analisisId }) {
  const [plantillas, setPlantillas] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [preview, setPreview] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloadingFmt, setDownloadingFmt] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/hipotecas/plantillas`, tokenHeader()).then((r) => {
      setPlantillas(r.data);
      if (r.data.length > 0) setSelectedId(r.data[0].id);
    });
  }, [open]);

  useEffect(() => {
    if (!open || !selectedId) return;
    setLoadingPreview(true);
    axios.get(`${API}/api/hipotecas/analisis/${analisisId}/preview-documento?plantilla_id=${selectedId}`, tokenHeader())
      .then((r) => setPreview(r.data.markdown))
      .catch(() => setPreview('Error generando preview'))
      .finally(() => setLoadingPreview(false));
  }, [selectedId, open, analisisId]);

  const descargar = async (fmt) => {
    setDownloadingFmt(fmt);
    try {
      await descargarArchivo(
        `${API}/api/hipotecas/analisis/${analisisId}/generar-documento?plantilla_id=${selectedId}&formato=${fmt}`,
        `documento_${selectedId}.${fmt}`,
      );
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setDownloadingFmt(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="dialog-generar-plantilla">
        <DialogHeader>
          <DialogTitle>Generar documento desde plantilla</DialogTitle>
        </DialogHeader>
        {plantillas.length === 0 ? (
          <div className="text-center py-10 text-sm text-gray-500">
            <p>No tienes plantillas configuradas.</p>
            <p className="mt-2">Ve a <strong>Configuración → Plantillas</strong> para añadir una.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 overflow-hidden">
            <div className="md:col-span-1 overflow-auto border-r pr-3">
              <p className="text-xs font-medium text-gray-500 mb-2">Plantillas disponibles</p>
              {plantillas.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className={`w-full text-left p-2 rounded mb-1 text-sm transition ${selectedId === p.id ? 'bg-purple-100 font-medium' : 'hover:bg-gray-50'}`}
                  data-testid={`plantilla-opt-${p.id}`}
                >
                  <p className="truncate">{p.nombre}</p>
                  <p className="text-xs text-gray-500">{p.tipo}</p>
                </button>
              ))}
            </div>
            <div className="md:col-span-2 overflow-auto">
              <p className="text-xs font-medium text-gray-500 mb-2">Vista previa (datos sustituidos)</p>
              {loadingPreview ? (
                <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-purple-600" /></div>
              ) : (
                <pre className="text-xs bg-gray-50 p-3 rounded whitespace-pre-wrap font-mono max-h-[55vh] overflow-auto" data-testid="preview-plantilla">{preview}</pre>
              )}
            </div>
          </div>
        )}
        <DialogFooter className="flex-shrink-0">
          {plantillas.length > 0 && selectedId && (
            <>
              <Button variant="outline" onClick={() => descargar('md')} disabled={!!downloadingFmt} data-testid="btn-descargar-plantilla-md">
                {downloadingFmt === 'md' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}MD
              </Button>
              <Button variant="outline" onClick={() => descargar('docx')} disabled={!!downloadingFmt} data-testid="btn-descargar-plantilla-docx">
                {downloadingFmt === 'docx' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}DOCX
              </Button>
              <Button onClick={() => descargar('pdf')} disabled={!!downloadingFmt} data-testid="btn-descargar-plantilla-pdf">
                {downloadingFmt === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}PDF
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Modal: Compartir informe con cliente (link público)
// ============================================================================
function CompartirInformeDialog({ open, onClose, analisisId }) {
  const [enlaces, setEnlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [dias, setDias] = useState(30);
  const [copiedToken, setCopiedToken] = useState(null);
  const [qrEnlace, setQrEnlace] = useState(null);
  const [emailEnlace, setEmailEnlace] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/hipotecas/analisis/${analisisId}/enlaces`, tokenHeader());
      // Reescribir url_publica usando el dominio actual del navegador (no el interno)
      const origin = window.location.origin;
      const enriched = r.data.map((e) => ({
        ...e,
        url_publica: `${origin}/informe-publico/${e.token}`,
      }));
      setEnlaces(enriched);
    } catch {
      setEnlaces([]);
    } finally {
      setLoading(false);
    }
  }, [analisisId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const crear = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/api/hipotecas/analisis/${analisisId}/compartir`, { dias_expiracion: dias }, tokenHeader());
      toast({ title: 'Enlace generado', description: `Válido durante ${dias} días.` });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  const revocar = async (id) => {
    if (!window.confirm('¿Revocar este enlace? El cliente ya no podrá acceder.')) return;
    try {
      await axios.delete(`${API}/api/hipotecas/enlaces/${id}`, tokenHeader());
      toast({ title: 'Enlace revocado' });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const enviarRecordatorio = async (id) => {
    if (!window.confirm('¿Enviar recordatorio por email al cliente?')) return;
    try {
      await axios.post(`${API}/api/hipotecas/enlaces/${id}/enviar-recordatorio`, {}, tokenHeader());
      toast({ title: 'Recordatorio enviado' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const copiar = (url, token) => {
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 1500);
    toast({ title: 'Enlace copiado al portapapeles' });
  };

  const formatFecha = (s) => s ? new Date(s).toLocaleString('es-ES') : '—';

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="dialog-compartir">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-purple-600" />
              Compartir informe con el cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 flex-1 overflow-auto">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-purple-900">
                Genera un enlace público (sin login) para que el cliente pueda ver el análisis y aceptar el encargo profesional desde cualquier dispositivo.
                Al aceptar, se creará automáticamente una <strong>reclamación extrajudicial en estado BORRADOR</strong> con sus datos
                y recibirás un email con el PDF adjunto.
              </p>
            </div>

            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label>Duración del enlace (días)</Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={dias}
                  onChange={(e) => setDias(parseInt(e.target.value, 10) || 30)}
                  data-testid="input-dias-expiracion"
                />
              </div>
              <Button onClick={crear} disabled={creating} data-testid="btn-crear-enlace">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
                Generar enlace
              </Button>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Enlaces existentes</p>
              {loading ? (
                <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-purple-600" /></div>
              ) : enlaces.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">Aún no has generado ningún enlace.</p>
              ) : (
                <div className="space-y-2" data-testid="lista-enlaces">
                  {enlaces.map((e) => {
                    const caducado = new Date(e.expira_en) < new Date();
                    const inactivo = e.revocado || caducado;
                    const visitadoSinAceptar = e.veces_visto > 0 && !e.aceptado_at && !inactivo;
                    return (
                      <div key={e.id} className={`border rounded-lg p-3 ${inactivo ? 'bg-gray-50 opacity-70' : 'bg-white'}`} data-testid={`enlace-${e.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              {e.aceptado_at ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />Aceptado
                                </Badge>
                              ) : e.revocado ? (
                                <Badge className="bg-red-100 text-red-800 border-red-300">
                                  <XCircle className="w-3 h-3 mr-1" />Revocado
                                </Badge>
                              ) : caducado ? (
                                <Badge variant="outline">Caducado</Badge>
                              ) : (
                                <Badge className="bg-blue-100 text-blue-800 border-blue-300">Activo</Badge>
                              )}
                              <span className="text-xs text-gray-500">
                                <Eye className="w-3 h-3 inline mr-1" />{e.veces_visto} vistas
                              </span>
                              {e.reclamacion_creada_id && (
                                <span className="text-xs text-purple-700">
                                  <Scale className="w-3 h-3 inline mr-1" />Reclamación #{e.reclamacion_creada_id}
                                </span>
                              )}
                            </div>
                            <code className="text-xs text-purple-700 break-all font-mono block bg-purple-50 p-2 rounded">
                              {e.url_publica}
                            </code>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-gray-500 mt-2">
                              <span>Creado: {formatFecha(e.created_at)}</span>
                              <span>Caduca: {formatFecha(e.expira_en)}</span>
                              {e.primera_visita_at && <span>1ª visita: {formatFecha(e.primera_visita_at)}</span>}
                              {e.aceptado_at && <span className="text-emerald-700">Aceptado: {formatFecha(e.aceptado_at)}</span>}
                              {e.firma_nombre && <span className="col-span-2">Por: {e.firma_nombre}</span>}
                            </div>
                          </div>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {!inactivo && (
                              <Button size="sm" variant="outline" onClick={() => copiar(e.url_publica, e.token)} data-testid={`btn-copiar-${e.id}`} title="Copiar enlace">
                                {copiedToken === e.token ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                              </Button>
                            )}
                            {!inactivo && (
                              <Button size="sm" variant="outline" onClick={() => setEmailEnlace(e)} data-testid={`btn-email-${e.id}`} title="Enviar por email">
                                <Mail className="w-4 h-4" />
                              </Button>
                            )}
                            {!inactivo && (
                              <Button size="sm" variant="outline" onClick={() => setQrEnlace(e)} data-testid={`btn-qr-${e.id}`} title="Ver QR">
                                <QrCode className="w-4 h-4" />
                              </Button>
                            )}
                            {visitadoSinAceptar && e.firma_email && (
                              <Button size="sm" variant="outline" onClick={() => enviarRecordatorio(e.id)} data-testid={`btn-recordar-${e.id}`} title="Enviar recordatorio">
                                <Bell className="w-4 h-4 text-amber-600" />
                              </Button>
                            )}
                            {!inactivo && (
                              <Button size="sm" variant="outline" onClick={() => window.open(e.url_publica, '_blank')} data-testid={`btn-abrir-${e.id}`} title="Abrir">
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            {!e.revocado && (
                              <Button size="sm" variant="ghost" onClick={() => revocar(e.id)} data-testid={`btn-revocar-${e.id}`} title="Revocar">
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal QR */}
      <QRDialog enlace={qrEnlace} onClose={() => setQrEnlace(null)} />

      {/* Modal Enviar Email */}
      <EnviarEnlaceEmailDialog
        enlace={emailEnlace}
        onClose={() => setEmailEnlace(null)}
      />
    </>
  );
}


function EnviarEnlaceEmailDialog({ enlace, onClose }) {  const [email, setEmail] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (enlace) {
      setEmail(enlace.firma_email || '');
      setMensaje('');
    }
  }, [enlace]);

  const enviar = async () => {
    if (!email || !email.includes('@')) {
      toast({ title: 'Email inválido', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/api/hipotecas/enlaces/${enlace.id}/enviar-email`, {
        email_destino: email,
        mensaje,
        url_publica: enlace.url_publica,
      }, tokenHeader());
      toast({ title: 'Email enviado al cliente' });
      onClose();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!enlace} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="dialog-email-enlace">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-purple-600" />Enviar enlace por email
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Email destino *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="input-email-destino" />
          </div>
          <div>
            <Label>Mensaje personalizado (opcional)</Label>
            <Textarea
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              rows={4}
              placeholder="Ej. Buenos días, le adjuntamos el análisis preliminar de su escritura..."
              data-testid="textarea-mensaje-email"
            />
          </div>
          <p className="text-xs text-gray-500">
            El cliente recibirá un email profesional con su informe y el enlace para aceptar el encargo.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={enviar} disabled={sending || !email} data-testid="btn-enviar-email-enlace">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Enviar email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function QRDialog({ enlace, onClose }) {
  const [qrUrl, setQrUrl] = useState(null);

  useEffect(() => {
    if (!enlace) {
      setQrUrl(null);
      return;
    }
    let active = true;
    let createdUrl = null;
    axios.get(
      `${API}/api/hipotecas/enlaces/${enlace.id}/qr?url=${encodeURIComponent(enlace.url_publica)}`,
      { ...tokenHeader(), responseType: 'blob' },
    ).then((r) => {
      if (!active) return;
      createdUrl = URL.createObjectURL(r.data);
      setQrUrl(createdUrl);
    });
    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [enlace]);

  return (
    <Dialog open={!!enlace} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md text-center" data-testid="dialog-qr">
        <DialogHeader>
          <DialogTitle>Código QR del enlace</DialogTitle>
        </DialogHeader>
        {enlace && (
          <div className="space-y-3">
            {qrUrl ? (
              <img
                src={qrUrl}
                alt="QR"
                className="mx-auto rounded-lg border-2 border-purple-200 p-2 bg-white"
                style={{ maxWidth: '280px' }}
                data-testid="img-qr"
              />
            ) : (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>
            )}
            <p className="text-sm text-gray-600">
              Muestra este QR al cliente o imprímelo. Al escanearlo accederá al informe personalizado.
            </p>
            <code className="text-xs text-purple-700 break-all block bg-purple-50 p-2 rounded">{enlace.url_publica}</code>
            {qrUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = qrUrl;
                  a.download = `qr_analisis_${enlace.id}.png`;
                  a.click();
                }}
                data-testid="btn-descargar-qr"
              >
                <FileDown className="w-4 h-4 mr-1" />Descargar QR
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
